/* Demand Vintage — Theme JS */
'use strict';

const ScrollLock = {
  depth: 0,
  lock() {
    this.depth += 1;
    if (this.depth > 1) return;
    document.documentElement.classList.add('is-locked');
    document.body.style.overflow = 'hidden';
  },
  unlock() {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth !== 0) return;
    document.documentElement.classList.remove('is-locked');
    document.body.style.overflow = '';
  }
};

function getActiveLocale() {
  return window.dvLocalization?.locale || document.documentElement.lang || 'en-GB';
}

function getActiveCurrency() {
  return window.dvLocalization?.currency || window.shopCurrency || 'GBP';
}

function formatMoney(cents) {
  return (cents / 100).toLocaleString(getActiveLocale(), {
    style: 'currency',
    currency: getActiveCurrency()
  });
}

function formatPredictivePrice(rawValue) {
  const rawString = String(rawValue ?? '').trim();
  if (!rawString) return '';

  const numeric = Number(rawString.replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return '';

  const amount = rawString.includes('.')
    ? numeric
    : Math.abs(numeric) >= 1000
      ? numeric / 100
      : numeric;

  return amount.toLocaleString(getActiveLocale(), {
    style: 'currency',
    currency: getActiveCurrency()
  });
}

function resetDraggedPanel(panel) {
  if (!panel) return;
  panel.style.transform = '';
  panel.style.opacity = '';
  panel.style.transition = '';
}

function bindSheetDismiss(handle, panel, onClose, options = {}) {
  if (!handle || !panel || typeof onClose !== 'function' || !window.PointerEvent) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const axis = options.axis === 'y' ? 'y' : 'x';
  const direction = options.direction || 'positive';
  const maxTravel = options.maxTravel || 180;
  const closeThreshold = options.closeThreshold || 72;
  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let travel = 0;
  let dragging = false;

  handle.style.touchAction = axis === 'y' ? 'pan-x' : 'pan-y';

  const release = () => {
    dragging = false;
    pointerId = null;
    travel = 0;
  };

  const applyTransform = offset => {
    const signedOffset = direction === 'negative' ? -offset : offset;
    panel.style.transform = axis === 'y'
      ? `translate3d(0, ${signedOffset}px, 0)`
      : `translate3d(${signedOffset}px, 0, 0)`;
    panel.style.opacity = String(Math.max(0.82, 1 - (offset / (maxTravel * 2.4))));
  };

  handle.addEventListener('pointerdown', event => {
    if (event.button !== undefined && event.button !== 0) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    travel = 0;
    dragging = true;
    panel.style.transition = 'none';
    handle.setPointerCapture?.(pointerId);
  });

  handle.addEventListener('pointermove', event => {
    if (!dragging || event.pointerId !== pointerId) return;

    const delta = axis === 'y'
      ? event.clientY - startY
      : event.clientX - startX;

    const offset = direction === 'negative'
      ? Math.max(0, delta * -1)
      : Math.max(0, delta);

    travel = Math.min(offset, maxTravel);
    applyTransform(travel);
  });

  const finishGesture = event => {
    if (!dragging || (event && event.pointerId !== pointerId)) return;

    panel.style.transition = `transform 320ms ${easing}, opacity 320ms ${easing}`;

    if (travel >= closeThreshold) {
      resetDraggedPanel(panel);
      onClose();
    } else {
      requestAnimationFrame(() => {
        panel.style.transform = '';
        panel.style.opacity = '';
        window.setTimeout(() => {
          panel.style.transition = '';
        }, 320);
      });
    }

    release();
  };

  handle.addEventListener('pointerup', finishGesture);
  handle.addEventListener('pointercancel', finishGesture);
}

/* ── Cart state ──────────────────────────────────────── */
const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(variantId, qty = 1, sellingPlan = '') {
    const payload = { id: variantId, quantity: qty };
    if (sellingPlan) payload.selling_plan = sellingPlan;

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return res.json();
  },
  async remove(key) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    return res.json();
  },
  async updateItem(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    return res.json();
  }
};

/* ── Cart Drawer ─────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.el = document.getElementById('cart-drawer');
    if (!this.el) return;
    this.overlay = this.el.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.el.querySelector('.cart-drawer__close');
    this.panel = this.el.querySelector('.cart-drawer__panel');
    this.grabber = this.el.querySelector('.cart-drawer__grabber');
    this.body = this.el.querySelector('.cart-drawer__body');
    this.footer = document.getElementById('cart-drawer-footer');
    this.countEls = document.querySelectorAll('.cart-count');

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    this.el.addEventListener('click', e => {
      const btn = e.target.closest('.cart-item__remove');
      if (btn) this.removeItem(btn.dataset.key);
    });

    this.el.addEventListener('change', e => {
      const sel = e.target.closest('.cart-item__qty-select');
      if (sel) this.updateQty(sel.dataset.key, parseInt(sel.value, 10));
    });

    bindSheetDismiss(this.grabber, this.panel, () => this.close(), {
      axis: 'x',
      direction: 'positive',
      maxTravel: 160,
      closeThreshold: 64
    });
  }

  open() {
    resetDraggedPanel(this.panel);
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    ScrollLock.lock();
    this.refresh();
  }

  close() {
    if (!this.el.classList.contains('is-open')) return;
    resetDraggedPanel(this.panel);
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
  }

  async refresh() {
    const cart = await Cart.get();
    this.updateCount(cart.item_count);
    this.renderItems(cart);
  }

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  qtyOptions(current) {
    return Array.from({ length: 10 }, (_, i) => i + 1)
      .map(n => `<option value="${n}"${n === current ? ' selected' : ''}>Qty ${n}</option>`)
      .join('');
  }

  renderItems(cart) {
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your bag is empty.</p>
          <a href="/collections/all" class="btn btn--primary" style="margin-top:2rem;display:inline-block;">Shop Now</a>
        </div>`;
      if (this.footer) this.footer.style.display = 'none';
      return;
    }

    if (this.footer) this.footer.style.display = 'block';

    this.body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
        </a>
        <div class="cart-item__details">
          <div class="cart-item__meta">
            <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
            <p class="cart-item__name">${item.product_title}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          </div>
          <div class="cart-item__actions">
            <div class="cart-item__qty-wrap">
              <select class="cart-item__qty-select" data-key="${item.key}" aria-label="Quantity">
                ${this.qtyOptions(item.quantity)}
              </select>
              <svg class="cart-item__qty-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
            </button>
          </div>
        </div>
      </div>
    `).join('');

    const totalEl = document.getElementById('cart-drawer-total');
    if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);
  }

  async removeItem(key) {
    await Cart.remove(key);
    this.refresh();
  }

  async updateQty(key, qty) {
    await Cart.updateItem(key, qty);
    this.refresh();
  }

  formatMoney(cents) {
    return formatMoney(cents);
  }
}

/* ── Mobile Nav ──────────────────────────────────────── */
class MobileNav {
  constructor() {
    this.el = document.getElementById('mobile-nav');
    if (!this.el) return;
    this.overlay = this.el.querySelector('.mobile-nav__overlay');
    this.closeBtn = this.el.querySelector('.mobile-nav__close');
    this.toggleBtn = document.getElementById('menu-toggle');
    this.panel = this.el.querySelector('.mobile-nav__panel');
    this.grabber = this.el.querySelector('.mobile-nav__grabber');

    this.toggleBtn?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.el.querySelectorAll('a').forEach(link => link.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    bindSheetDismiss(this.grabber, this.panel, () => this.close(), {
      axis: 'x',
      direction: 'negative',
      maxTravel: 160,
      closeThreshold: 64
    });
  }

  open() {
    resetDraggedPanel(this.panel);
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    ScrollLock.lock();
  }

  close() {
    if (!this.el.classList.contains('is-open')) return;
    resetDraggedPanel(this.panel);
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    ScrollLock.unlock();
  }
}

/* ── Search Drawer (with predictive results) ─────────── */
class SearchDrawer {
  constructor() {
    this.el = document.getElementById('search-drawer');
    if (!this.el) return;
    this.overlay   = this.el.querySelector('.search-drawer__overlay');
    this.closeBtn  = this.el.querySelector('.search-drawer__close');
    this.clearBtn  = this.el.querySelector('.search-drawer__clear');
    this.input     = this.el.querySelector('.search-drawer__input');
    this.results   = document.getElementById('search-results');
    this.hints     = document.getElementById('search-hints');
    this.panel     = this.el.querySelector('.search-drawer__panel');
    this.grabber   = this.el.querySelector('.search-drawer__grabber');
    this.debounce  = null;

    document.getElementById('search-toggle')?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.clearBtn?.addEventListener('click', () => { this.input.value = ''; this.input.focus(); this.showHints(); });
    this.input?.addEventListener('input', () => this.onInput());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    bindSheetDismiss(this.grabber, this.panel, () => this.close(), {
      axis: 'y',
      direction: 'positive',
      maxTravel: 140,
      closeThreshold: 54
    });
  }

  open() {
    resetDraggedPanel(this.panel);
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    ScrollLock.lock();
    this.showHints();
    this.input?.focus();
  }

  close() {
    if (!this.el.classList.contains('is-open')) return;
    resetDraggedPanel(this.panel);
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
  }

  showHints() {
    this.clearBtn.hidden = true;
    this.results.hidden = true;
    this.hints.hidden = false;
  }

  onInput() {
    const q = this.input.value.trim();
    this.clearBtn.hidden = q.length === 0;
    clearTimeout(this.debounce);
    if (q.length < 2) { this.showHints(); return; }
    this.debounce = setTimeout(() => this.fetchSuggestions(q), 250);
  }

  async fetchSuggestions(q) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`;
      const res = await fetch(url);
      const json = await res.json();
      const products = json?.resources?.results?.products ?? [];
      this.renderResults(products, q);
    } catch (_) {
      this.showHints();
    }
  }

  renderResults(products, q) {
    this.results.hidden = false;

    if (products.length === 0) {
      this.results.innerHTML = `<p class="search-drawer__no-results">No results for "<strong>${q}</strong>" — try a brand name or category</p>`;
      this.hints.hidden = false;
      return;
    }

    this.hints.hidden = true;

    this.results.innerHTML = products.map(p => {
      const imgUrl = p.image;
      const img = imgUrl
        ? `<img class="search-result-card__img" src="${imgUrl}" alt="${p.title.replace(/"/g,'&quot;')}" loading="lazy">`
        : `<div class="search-result-card__img-placeholder">🧥</div>`;
      const price = p.price ? formatPredictivePrice(p.price) : '';
      return `<a class="search-result-card" href="${p.url}">
        ${img}
        <div class="search-result-card__info">
          <p class="search-result-card__title">${p.title}</p>
          ${price ? `<p class="search-result-card__price">${price}</p>` : ''}
        </div>
      </a>`;
    }).join('');
  }
}

/* ── Product Gallery ─────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.mainImg = el.querySelector('.product-gallery__main img');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    if (!this.mainImg || !this.thumbs.length) return;
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.setActive(i)));
  }

  setActive(index) {
    const thumb = this.thumbs[index];
    const src = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    if (src) this.mainImg.src = src;
    if (srcset) this.mainImg.srcset = srcset;
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}

/* ── Variant Selector ────────────────────────────────── */
class VariantSelector {
  constructor(form) {
    this.form = form;
    try {
      const jsonEl = document.getElementById('product-json-data');
      this.productData = jsonEl ? JSON.parse(jsonEl.textContent) : {};
    } catch (e) {
      this.productData = {};
    }
    try {
      const invEl = document.getElementById('product-inventory-data');
      this.inventoryData = invEl ? JSON.parse(invEl.textContent) : {};
    } catch (e) {
      this.inventoryData = {};
    }
    try {
      const plansEl = document.getElementById('product-selling-plan-data');
      this.sellingPlanData = plansEl ? JSON.parse(plansEl.textContent) : {};
    } catch (e) {
      this.sellingPlanData = {};
    }
    this.priceEl = document.querySelector('.price__current');
    this.comparePriceEl = document.querySelector('.price__compare');
    this.atcBtn = form.querySelector('.product-form__atc');
    this.stockBar = document.getElementById('product-stock-bar');
    this.swatches = form.querySelectorAll('.swatch-btn');
    this.selectedVariant = this.getVariantById(form.querySelector('input[name="id"]')?.value);

    this.swatches.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-sold-out')) return;
        const group = btn.closest('.swatch-grid');
        group.querySelectorAll('.swatch-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.updateVariant();
      });
    });

    this.form.addEventListener('change', e => {
      if (!e.target.matches('input[name="selling_plan"], select[name="selling_plan"], input[name="selling_plan_choice"]')) return;
      if (this.selectedVariant) this.updatePrice(this.selectedVariant);
    });
  }

  getSelectedOptions() {
    const opts = [];
    this.form.querySelectorAll('.swatch-grid').forEach(grid => {
      const active = grid.querySelector('.swatch-btn.is-active');
      if (active) opts.push(active.dataset.value);
    });
    return opts;
  }

  findVariant(options) {
    return (this.productData.variants || []).find(v =>
      v.options.every((o, i) => o === options[i])
    );
  }

  getVariantById(id) {
    return (this.productData.variants || []).find(v => String(v.id) === String(id));
  }

  getSelectedSellingPlanId() {
    const checkedPlan = this.form.querySelector('input[name="selling_plan"]:checked');
    if (checkedPlan) return checkedPlan.value;

    const planSelect = this.form.querySelector('select[name="selling_plan"]');
    if (planSelect) return planSelect.value;

    const hiddenPlans = Array.from(this.form.querySelectorAll('input[name="selling_plan"][type="hidden"]'))
      .map(input => input.value)
      .filter(Boolean);
    if (hiddenPlans.length) return hiddenPlans[hiddenPlans.length - 1];

    const checkedChoice = this.form.querySelector('input[name="selling_plan_choice"]:checked');
    return checkedChoice?.value || '';
  }

  formatMoney(cents) {
    return formatMoney(cents);
  }

  updatePrice(variant) {
    const sellingPlanId = this.getSelectedSellingPlanId();
    const allocation = sellingPlanId ? this.sellingPlanData?.[String(variant.id)]?.[String(sellingPlanId)] : null;
    const price = allocation?.price ?? variant.price;
    const comparePrice = allocation?.compare_at_price ?? variant.compare_at_price;

    if (this.priceEl) this.priceEl.textContent = this.formatMoney(price);

    if (this.comparePriceEl) {
      if (comparePrice && comparePrice > price) {
        this.comparePriceEl.textContent = this.formatMoney(comparePrice);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }
  }

  updateVariant() {
    const options = this.getSelectedOptions();
    const variant = this.findVariant(options);
    if (!variant) return;
    this.selectedVariant = variant;

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Update hidden variant input
    const input = this.form.querySelector('input[name="id"]');
    if (input) input.value = variant.id;

    // Update price
    this.updatePrice(variant);

    // Update ATC button
    if (this.atcBtn) {
      if (variant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.textContent = 'Add to Bag';
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Sold Out';
      }
    }

    // Update inventory bar
    if (this.stockBar) {
      const invData = this.inventoryData[variant.id] || {};
      const inv = invData.inventory_quantity ?? 0;
      const tracked = invData.inventory_management === 'shopify';
      let html = '';
      if (variant.available) {
        if (tracked && inv === 1) {
          html = `<span class="product-stock product-stock--unique">One-of-one — when it's gone, it's gone.</span>`;
        } else if (tracked && inv <= 5) {
          html = `<span class="product-stock product-stock--urgent">Only ${inv} left — selling fast</span>`;
        } else if (tracked && inv <= 15) {
          html = `<span class="product-stock product-stock--low">Low stock — ${inv} remaining</span>`;
        } else {
          html = `<span class="product-stock product-stock--available">&#10003; In Stock</span>`;
        }
      } else {
        html = `<span class="product-stock product-stock--out">Out of Stock</span>`;
      }
      this.stockBar.innerHTML = html;
    }
  }
}

/* ── Add to Cart form ────────────────────────────────── */
class AddToCartForm {
  constructor(form, cartDrawer) {
    this.form = form;
    this.cartDrawer = cartDrawer;
    this.btn = form.querySelector('.product-form__atc');
    form.addEventListener('submit', e => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    const id = this.form.querySelector('input[name="id"]')?.value;
    if (!id) return;
    const formData = new FormData(this.form);
    const qty = parseInt(formData.get('quantity') || '1', 10);
    const sellingPlanValues = formData.getAll('selling_plan').filter(Boolean);
    const sellingPlan = sellingPlanValues[sellingPlanValues.length - 1] || formData.get('selling_plan_choice') || '';
    const originalText = this.btn.textContent;
    this.btn.textContent = 'Adding…';
    this.btn.disabled = true;
    try {
      await Cart.add(id, Number.isFinite(qty) ? qty : 1, sellingPlan);
      this.btn.textContent = 'Added!';
      setTimeout(() => {
        this.btn.textContent = originalText;
        this.btn.disabled = false;
      }, 1200);
      this.cartDrawer?.open();
    } catch (err) {
      this.btn.textContent = 'Error — try again';
      setTimeout(() => { this.btn.textContent = originalText; this.btn.disabled = false; }, 2000);
    }
  }
}

/* ── Accordion ───────────────────────────────────────── */
function initAccordions() {
  const setAccordionHeight = body => {
    if (!body) return;
    body.style.setProperty('--accordion-open-height', `${body.scrollHeight}px`);
  };

  document.querySelectorAll('.accordion__trigger').forEach(trigger => {
    const body = trigger.nextElementSibling;
    if (trigger.getAttribute('aria-expanded') === 'true' && body) {
      body.classList.add('is-open');
      setAccordionHeight(body);
    }

    trigger.addEventListener('click', () => {
      const body = trigger.nextElementSibling;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isOpen);
      body.classList.toggle('is-open', !isOpen);
      if (!isOpen) setAccordionHeight(body);
    });
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll('.accordion__body.is-open').forEach(setAccordionHeight);
  });
  window.addEventListener('load', () => {
    document.querySelectorAll('.accordion__body.is-open').forEach(setAccordionHeight);
  });
}

/* ── Nav dropdowns ───────────────────────────────────── */
function initNavDropdowns() {
  document.querySelectorAll('.site-nav__item--has-dropdown').forEach(item => {
    let closeTimer;
    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      item.classList.add('is-open');
    });
    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => item.classList.remove('is-open'), 150);
    });
  });
}

/* ── Cart toggle ─────────────────────────────────────── */
function initCartToggle(cartDrawer) {
  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', e => { e.preventDefault(); cartDrawer?.open(); });
  });
}

/* ── Quick add (product cards) ───────────────────────── */
function initQuickAdd(cartDrawer) {
  document.addEventListener('click', e => {
    const btn = e.target.closest('.pc-quickadd[data-variant]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    const id = btn.getAttribute('data-variant');
    const original = btn.textContent;
    btn.classList.add('is-loading');
    btn.textContent = 'Adding…';
    Cart.add(id)
      .then(() => {
        btn.textContent = 'Added ✓';
        btn.classList.add('is-added');
        cartDrawer?.refresh();
        cartDrawer?.open();
        Cart.get().then(cart => {
          document.querySelectorAll('.cart-count').forEach(el => {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? 'flex' : 'none';
          });
        });
        setTimeout(() => { btn.textContent = original; btn.classList.remove('is-loading', 'is-added'); }, 1400);
      })
      .catch(() => { btn.textContent = original; btn.classList.remove('is-loading'); });
  });
}

/* ── Selling plans / subscriptions ──────────────────── */
function initSellingPlans() {
  document.querySelectorAll('[data-selling-plan-box]').forEach(box => {
    const hidden = box.querySelector('input[name="selling_plan"]');
    if (!hidden) return;

    box.addEventListener('change', e => {
      const choice = e.target.closest('input[name="selling_plan_choice"]');
      if (!choice) return;
      hidden.value = choice.value || '';
    });
  });
}

/* ── Product card reveal animations ─────────────────── */
function initProductCardAnimations() {
  const cards = Array.from(document.querySelectorAll('.product-card-wrap:not([data-vb-card-observed])'));
  if (!cards.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    cards.forEach(card => card.classList.add('vb-card-in'));
    return;
  }

  document.documentElement.classList.add('vb-card-anim-ready');
  cards.forEach((card, index) => {
    card.setAttribute('data-vb-card-observed', 'true');
    card.style.setProperty('--vb-card-index', index % 12);
  });

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('vb-card-in');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  requestAnimationFrame(() => {
    cards.forEach(card => observer.observe(card));
  });
}

document.addEventListener('shopify:section:load', initProductCardAnimations);

/* ── Hero scroll motion ─────────────────────────────── */
function initHeroScroll() {
  const heroes = Array.from(document.querySelectorAll('[data-hero-scroll]'));
  if (!heroes.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    heroes.forEach(hero => {
      hero.style.setProperty('--hero-media-shift', '0px');
      hero.style.setProperty('--hero-content-shift', '0px');
      hero.style.setProperty('--hero-content-scale', '1');
      hero.style.setProperty('--hero-overlay-opacity', '1');
    });
    return;
  }

  let ticking = false;

  const update = () => {
    heroes.forEach(hero => {
      const rect = hero.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      const distance = Math.max(rect.height, 1);
      const progress = Math.max(0, Math.min(1, -rect.top / distance));
      const inView = rect.bottom > 0 && rect.top < viewport;

      if (!inView && rect.top >= viewport) {
        hero.style.setProperty('--hero-media-shift', '0px');
        hero.style.setProperty('--hero-content-shift', '0px');
        hero.style.setProperty('--hero-content-scale', '1');
        hero.style.setProperty('--hero-overlay-opacity', '0.9');
        return;
      }

      const mediaShift = `${(progress * 34).toFixed(2)}px`;
      const contentShift = `${(progress * -24).toFixed(2)}px`;
      const contentScale = (1 - (progress * 0.03)).toFixed(4);
      const overlayOpacity = Math.min(1, 0.9 + (progress * 0.1)).toFixed(4);

      hero.style.setProperty('--hero-media-shift', mediaShift);
      hero.style.setProperty('--hero-content-shift', contentShift);
      hero.style.setProperty('--hero-content-scale', contentScale);
      hero.style.setProperty('--hero-overlay-opacity', overlayOpacity);
    });
    ticking = false;
  };

  const requestTick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick);
}

/* ── Init ────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = new CartDrawer();
  // Expose for quick-add (collection grid) and other components
  window.Cart = Cart;
  window.cartDrawer = cartDrawer;
  new MobileNav();
  new SearchDrawer();

  const gallery = document.querySelector('.product-gallery');
  if (gallery) new ProductGallery(gallery);

  const productForm = document.querySelector('.product-form');
  if (productForm) {
    new VariantSelector(productForm);
    new AddToCartForm(productForm, cartDrawer);
  }

  initAccordions();
  initCartToggle(cartDrawer);
  initQuickAdd(cartDrawer);
  initSellingPlans();
  initProductCardAnimations();
  initNavDropdowns();
  initHeroScroll();

  // Init cart count on load
  Cart.get().then(cart => {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  });
});
